'use client'

import React, { createContext, useContext, useEffect, useRef, useCallback } from 'react'
import { useSession } from 'next-auth/react'
import { io, Socket } from 'socket.io-client'

type OrderEventCallback = (order: any) => void
type WalletEventCallback = (invoice: any) => void
type CreditEventCallback = (invoice: any) => void
type ChatActivityCallback = (activity: { sessionId: string | null }) => void

interface OrderSocketContextType {
  onNewOrder: (callback: OrderEventCallback) => () => void
  onOrderPaid: (callback: OrderEventCallback) => () => void
  onWalletPaid: (callback: WalletEventCallback) => () => void
  onCreditPaid: (callback: CreditEventCallback) => () => void
  onChatActivity: (callback: ChatActivityCallback) => () => void
}

const OrderSocketContext = createContext<OrderSocketContextType | undefined>(undefined)

export function OrderSocketProvider({ children }: { children: React.ReactNode }) {
  const { data: session } = useSession()
  const socketRef = useRef<Socket | null>(null)
  const listenersRef = useRef<Set<OrderEventCallback>>(new Set())
  const paidListenersRef = useRef<Set<OrderEventCallback>>(new Set())
  const walletListenersRef = useRef<Set<WalletEventCallback>>(new Set())
  const creditListenersRef = useRef<Set<CreditEventCallback>>(new Set())
  const chatListenersRef = useRef<Set<ChatActivityCallback>>(new Set())

  useEffect(() => {
    if (!session?.user?.accessToken || !session?.user?.vendor?.id) return

    const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:3001'

    const socketInstance = io(backendUrl, {
      path: '/order-events/',
      auth: {
        token: session.user.accessToken,
        vendorId: session.user.vendor.id
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10
    })

    socketInstance.on('order:new', (order: any) => {
      listenersRef.current.forEach(cb => cb(order))
    })

    socketInstance.on('order:paid', (order: any) => {
      paidListenersRef.current.forEach(cb => cb(order))
    })

    socketInstance.on('wallet:paid', (invoice: any) => {
      walletListenersRef.current.forEach(cb => cb(invoice))
    })

    socketInstance.on('credits:paid', (invoice: any) => {
      creditListenersRef.current.forEach(cb => cb(invoice))
    })

    socketInstance.on('chat:activity', (activity: { sessionId: string }) => {
      chatListenersRef.current.forEach(cb => cb(activity))
    })

    socketInstance.io.on('reconnect', () => {
      chatListenersRef.current.forEach(cb => cb({ sessionId: null }))
    })

    socketRef.current = socketInstance

    return () => {
      socketInstance.disconnect()
      socketRef.current = null
    }
  }, [session?.user?.accessToken, session?.user?.vendor?.id])

  const onNewOrder = useCallback((callback: OrderEventCallback) => {
    listenersRef.current.add(callback)
    return () => { listenersRef.current.delete(callback) }
  }, [])

  const onOrderPaid = useCallback((callback: OrderEventCallback) => {
    paidListenersRef.current.add(callback)
    return () => { paidListenersRef.current.delete(callback) }
  }, [])

  const onWalletPaid = useCallback((callback: WalletEventCallback) => {
    walletListenersRef.current.add(callback)
    return () => { walletListenersRef.current.delete(callback) }
  }, [])

  const onCreditPaid = useCallback((callback: CreditEventCallback) => {
    creditListenersRef.current.add(callback)
    return () => { creditListenersRef.current.delete(callback) }
  }, [])

  const onChatActivity = useCallback((callback: ChatActivityCallback) => {
    chatListenersRef.current.add(callback)
    return () => { chatListenersRef.current.delete(callback) }
  }, [])

  return (
    <OrderSocketContext.Provider value={{ onNewOrder, onOrderPaid, onWalletPaid, onCreditPaid, onChatActivity }}>
      {children}
    </OrderSocketContext.Provider>
  )
}

export function useOrderSocket() {
  const context = useContext(OrderSocketContext)
  if (context === undefined) {
    throw new Error('useOrderSocket must be used within OrderSocketProvider')
  }
  return context
}
